import React, { useEffect, useState } from 'react';
import Card from '@components-clean/core/card';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { SiteReviewDto } from '@interfaces/central-monitoring';
import { useLocalState, useTranslation } from '@hooks/index';
import SiteDatasetReviewsApi from '@apis/bff-api/central-monitoring/site-dataset-reviews';

interface IProps {
    studyRef: string;
    siteId?: number;
    siteName?: string;
}

export type FormattedStudyReviews = {
    reviewedStatus: string;
} & SiteReviewDto;

const ReviewHistoryGrid = ({ studyRef, siteId, siteName }: IProps) => {
    const { t } = useTranslation();
    const [datasetReviews, setDatasetReviews] = useState<FormattedStudyReviews[]>([]);
    const { data, isLoading, isValidating } = SiteDatasetReviewsApi.useGetAll(studyRef, siteId);
    const { TooltipCell } = createGridCustomCells();
    const isFetching = isLoading || isValidating;
    const reviewedStatusKeys = ['reviewed', 'unreviewed'];
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;
    const datasetDate = localState?.selectedCM2Dataset?.dataCutDate;

    useEffect(() => {
        const formattedDatasets = data
            ? data?.map((siteReview: SiteReviewDto) => ({
                  ...siteReview,
                  reviewedStatus: siteReview.reviewed ? 'Reviewed' : 'Unreviewed',
              }))
            : [];
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDatasetReviews(formattedDatasets ?? []);
    }, [data]);

    const DataSetReviewsColumns: GridColumnProps[] = [
        {
            id: 'datasetDate',
            field: 'datasetDate',
            title: 'Dataset Date',
            width: 1,
            minWidth: 140,
            locked: true,
            filter: 'date',
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            id: 'site',
            field: 'siteName',
            title: 'Site',
            width: 1,
            minWidth: 120,
            filter: 'text',
            locked: true,
        },
        {
            id: 'reviewed',
            field: 'reviewedStatus',
            title: 'Review Status',
            width: 1,
            minWidth: 120,
            filter: 'text',
            locked: true,
            lovData: reviewedStatusKeys.map((key) => t(`cm:${key}`)),
        },
        {
            id: 'summary',
            field: 'summary',
            title: 'Review Summary',
            cells: { data: TooltipCell() },
            width: 1,
            minWidth: 200,
            filter: 'text',
            locked: false,
        },
        {
            id: 'startDate',
            field: 'startDate',
            title: 'Reviewed Start Date',
            width: 1,
            minWidth: 140,
            locked: false,
            filter: 'date',
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            id: 'completedDate',
            field: 'completedDate',
            title: 'Reviewed End Date',
            width: 1,
            minWidth: 140,
            locked: false,
            filter: 'date',
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            id: 'reviewer',
            field: 'reviewer',
            title: 'Reviewer',
            width: 1,
            minWidth: 140,
            filter: 'text',
            locked: false,
        },
    ];

    if (!datasetReviews || isLoading) {
        return (
            <Card className="loading-card" data-testid="loading-card">
                <Loading />
            </Card>
        );
    }

    return (
        <Grid
            title="Review History"
            maxHeight="auto"
            testDataId="site-dataset-review-grid"
            data={datasetReviews}
            columns={DataSetReviewsColumns}
            loading={isFetching}
            useLoadingOverlay
            exportFilename={`${studyName}_${siteName}_${datasetDate}_Site_Dataset_Review_History`}
            showAddIcon={false}
        />
    );
};

export default ReviewHistoryGrid;
